import { SyntheticEvent } from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Box,
    Typography,
    Slider,
    CircularProgress,
    Badge,
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    FilterList as FilterListIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Shield as ShieldIcon,
    Logout as LogoutIcon,
    AssignmentInd as CategoryRequestIcon,
    ConfirmationNumber as ManageCategoryRequestIcon,
    EventNote as TemplateSetIcon,
    DateRange as YearSummaryIcon,
    EventAvailable as PersonalForecastIcon,
    MenuBook as HandbookIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggle, open, close, setForecast } from '@/store/uiSlice';
import {
    useGetSettingsQuery,
    useLogoutMutation,
    useGetPlanResourcesQuery,
    useGetUserInfoQuery,
    useGetAdminCategoryRequestsQuery,
    useGetResponsibleCategoryRequestsQuery,
} from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';

interface NavigationDrawerProps {
    isAdmin: boolean;
}

const Menu = ({ isAdmin }: NavigationDrawerProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { data: settings } = useGetSettingsQuery();
    const { menu, history } = useAppSelector((state) => state.ui.open);
    const forecast = useAppSelector((state) => state.ui.forecast);
    const { LL } = useI18nContext();
    const [logout] = useLogoutMutation();

    // Get loading state for plan resources query
    const { isLoading: planLoading } = useGetPlanResourcesQuery(
        { forecast, includeHistory: history },
        { skip: false }
    );

    // Local state for slider to prevent expensive API calls during dragging
    const [localForecast, setLocalForecast] = useState(forecast);

    // Sync local state with Redux state when forecast changes externally
    useEffect(() => {
        setLocalForecast(forecast);
    }, [forecast]);

    const handleClose = () => dispatch(close('menu'));

    const handleMenuItemClick = (action: () => void) => {
        action();
        handleClose();
    };

    const handleForecastChange = (_event: Event, newValue: number | number[]) => {
        setLocalForecast(newValue as number);
    };

    const handleForecastChangeCommitted = (
        _event: Event | SyntheticEvent,
        newValue: number | number[]
    ) => {
        dispatch(setForecast(newValue as number));
    };

    const handleLogout = async () => {
        handleClose();
        try {
            const result = await logout().unwrap();

            // If OIDC user, redirect to provider's logout page
            if (result.oidc && result.logoutUrl) {
                window.location.href = result.logoutUrl;
            } else {
                // Local auth user - redirect to login page
                window.location.href = '/';
            }
        } catch (error) {
            console.error('Logout failed:', error);
            // Fallback: redirect to home anyway
            window.location.href = '/';
        }
    };

    const { data: userInfo } = useGetUserInfoQuery();

    const isResponsible = (userInfo?.categoriesResponsible?.length ?? 0) > 0;
    const canReviewRequests = isAdmin || isResponsible;

    const adminPending = useGetAdminCategoryRequestsQuery(
        { status: 'pending' },
        { skip: !canReviewRequests || !isAdmin }
    );
    const responsiblePending = useGetResponsibleCategoryRequestsQuery(
        { status: 'pending' },
        { skip: !canReviewRequests || isAdmin || !isResponsible }
    );

    const pendingCount = isAdmin
        ? (adminPending.data?.data?.length ?? 0)
        : (responsiblePending.data?.data?.length ?? 0);

    const canEditTemplateSets = !!(userInfo?.admin || userInfo?.scheduleTemplateEditor);

    return (
        <Drawer anchor="left" open={menu} onClose={handleClose}>
            <List sx={{ width: 250 }}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(() => dispatch(open('user')))}
                    >
                        <ListItemIcon>
                            <PersonIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={LL.menu.profile()} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(() => dispatch(open('filter')))}
                    >
                        <ListItemIcon>
                            <FilterListIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={LL.menu.filterCategories()} />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(() => dispatch(open('categoryRequest')))}
                    >
                        <ListItemIcon>
                            <CategoryRequestIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={LL.menu.manageMyCategories()} />
                    </ListItemButton>
                </ListItem>
                {settings?.mail?.enabled && (
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => handleMenuItemClick(() => dispatch(open('mailbox')))}
                        >
                            <ListItemIcon>
                                <EmailIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={LL.menu.mailbox()} />
                        </ListItemButton>
                    </ListItem>
                )}

                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(() => dispatch(open('yearSummary')))}
                    >
                        <ListItemIcon>
                            <YearSummaryIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={LL.menu.yearSummary()} />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() =>
                            handleMenuItemClick(() => dispatch(open('personalForecast')))
                        }
                    >
                        <ListItemIcon>
                            <PersonalForecastIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={LL.menu.personalForecast()} />
                    </ListItemButton>
                </ListItem>

                <Box sx={{ px: 2, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {LL.menu.forecast()}
                        </Typography>
                        {planLoading && <CircularProgress size={16} thickness={5} />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                            value={localForecast}
                            onChange={handleForecastChange}
                            onChangeCommitted={handleForecastChangeCommitted}
                            disabled={planLoading}
                            min={5}
                            max={45}
                            valueLabelDisplay="auto"
                            sx={{ flex: 1 }}
                            marks={[
                                { value: 5, label: '5' },
                                { value: 15, label: '15' },
                                { value: 30, label: '30' },
                                { value: 45, label: '45' },
                            ]}
                        />
                    </Box>
                </Box>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(() => dispatch(toggle('history')))}
                    >
                        <ListItemIcon>
                            <HistoryIcon color={history ? 'primary' : 'inherit'} />
                        </ListItemIcon>
                        <ListItemText
                            primary={history ? LL.menu.hideHistory() : LL.menu.showHistory()}
                        />
                    </ListItemButton>
                </ListItem>

                {(isAdmin ||
                    (canEditTemplateSets && settings?.schedule?.enabled) ||
                    canReviewRequests) && (
                    <>
                        <Divider sx={{ my: 1 }} />

                        {canReviewRequests && (
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() =>
                                        handleMenuItemClick(() =>
                                            dispatch(open('categoryRequestsReview'))
                                        )
                                    }
                                >
                                    <ListItemIcon>
                                        <Badge
                                            color="warning"
                                            badgeContent={pendingCount}
                                            overlap="rectangular"
                                            anchorOrigin={{ vertical: 'bottom' }}
                                            invisible={pendingCount === 0}
                                        >
                                            <ManageCategoryRequestIcon color="error" />
                                        </Badge>
                                    </ListItemIcon>
                                    <ListItemText primary={LL.menu.reviewCategoryRequests()} />
                                </ListItemButton>
                            </ListItem>
                        )}

                        {canEditTemplateSets && settings?.schedule?.enabled && (
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() =>
                                        handleMenuItemClick(() =>
                                            dispatch(open('scheduleTemplateSets'))
                                        )
                                    }
                                >
                                    <ListItemIcon>
                                        <TemplateSetIcon color="error" />
                                    </ListItemIcon>
                                    <ListItemText primary={LL.menu.scheduleTemplates()} />
                                </ListItemButton>
                            </ListItem>
                        )}

                        {isAdmin && (
                            <>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() =>
                                            handleMenuItemClick(() => dispatch(open('event')))
                                        }
                                    >
                                        <ListItemIcon>
                                            <AddIcon color="error" />
                                        </ListItemIcon>
                                        <ListItemText primary={LL.menu.addEvent()} />
                                    </ListItemButton>
                                </ListItem>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() =>
                                            handleMenuItemClick(() => navigate('/admin'))
                                        }
                                    >
                                        <ListItemIcon>
                                            <ShieldIcon color="error" />
                                        </ListItemIcon>
                                        <ListItemText primary={LL.menu.adminPanel()} />
                                    </ListItemButton>
                                </ListItem>
                            </>
                        )}
                    </>
                )}

                <Divider sx={{ my: 1 }} />

                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(() => dispatch(open('handbook')))}
                    >
                        <ListItemIcon>
                            <HandbookIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={LL.handbook.menuLabel()} />
                    </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                    <ListItemButton onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon color="action" />
                        </ListItemIcon>
                        <ListItemText primary={LL.appBar.logout()} />
                    </ListItemButton>
                </ListItem>
            </List>
        </Drawer>
    );
};

export default Menu;
